<template>
  <div id="aoetbt">
    <h5>{{ t('checkout.CreditCartHeader') }}</h5>
    <div id="aoetbtNameOnCard" class="form-control" />
    <div id="aoetbtCreditCardNumber" class="form-control" />
    <div id="aoetbtCreditCardCVV" class="form-control SecurityCode" />
    <div id="aoetbtCreditCardExpirationDate" class="form-control" />
    <div class="row mt-3">
      <div class="col-12 col-md-3 braintree-charge-label-div">
        {{ t('checkout.PaymentAmount') }}
      </div>
      <div class="col-12 col-md-3 braintree-charge-amount-div">
        <input
          type="text"
          class="form-control"
          id="aoetbtPaymentAmount"
          v-model="chargeDisplay"
          readonly
          :data-amount="charge"
        />
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 text-center braintree-submit-button">
        <button id="aoetbtSubmit" class="btn primaryBtn">{{ t(props.submitbuttonmessage) }}</button>
      </div>
    </div>
    <input type="hidden" id="aoetbtNonce" />
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { ref, onMounted, watch } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import type { IAoeCart } from '@/types/CartTypes.ts'
import { CartPaymentTokenCreate } from '@/api/Cart'
import { GetApiKey, GetOrderEntryConnectorUrl } from '@/stores/SettingsStore.ts'
import { AoeBraintree } from './AoeBraintree.ts'
import { getFunnelType } from '@/analytics/index'
import { FormatCurrency } from '@/services/currencyFormatter'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
const props = defineProps<{
  cart: IAoeCart | null
  name: string
  systemKeyword: string,
  check3DS?: boolean
  submitbuttonmessage: string
}>()

const logger = useLogger()
logger.debug(
  'BraintreePaymentEntry props',
  {
    name: props.name,
    systemKeyword: props.systemKeyword,
    check3DS: props.check3DS === true,
    aoeCartIdToken: props.cart?.aoeCartIdToken,
    balance: props.cart?.balance,
  },
)
const charge = ref<number>(props.cart ? props.cart.balance : 0)
const chargeDisplay = ref<string>('')

// Initialize display value with formatted currency
chargeDisplay.value = FormatCurrency(charge.value, null) || charge.value.toFixed(2)

// Watch for changes to cart balance and update charge amount
watch(() => props.cart?.balance, (newBalance) => {
  if (newBalance !== undefined) {
    logger.debug('Cart balance changed:', newBalance)
    charge.value = newBalance
    chargeDisplay.value = FormatCurrency(newBalance, null) || newBalance.toFixed(2)
  }
}, { immediate: true })

onMounted(async () => {
  logger.debug('onMounted')

  if (props.cart == null) {
    logger.error('props.cart is null')
    return
  }

  const response = await CartPaymentTokenCreate({
    aoeCartIdToken: props.cart.aoeCartIdToken,
    systemKeyword: props.systemKeyword,
    charge: props.cart.balance,
  })

  logger.debug('response:', response)
  if (response.item.ClientToken) {
    AoeBraintree.clientToken = response.item.ClientToken
    AoeBraintree.enableThreeDSecure = props.check3DS === true
    AoeBraintree.hostedFieldsText.cardholderName = t('labels.CardHolderName')
    AoeBraintree.hostedFieldsText.creditCardNumber = t('labels.CardNumber')
    AoeBraintree.hostedFieldsText.cvv = t('labels.CardSecurityCode')
    AoeBraintree.hostedFieldsText.expirationDate = t(
      'labels.CardExpirationDate',
    )
    AoeBraintree.sourceValues.aOECartIdToken = props.cart.aoeCartIdToken
    AoeBraintree.sourceValues.systemKeyword = props.systemKeyword
    ;(AoeBraintree as any).cartTotal = props.cart.total
    ;(AoeBraintree as any).cartCurrency = props.cart.currencyId || 'EUR'
    ;(AoeBraintree as any).cartFunnelType = getFunnelType(props.cart.orderTypeId, props.cart.eventId)
    AoeBraintree.createPaymentUrl =
      await GetOrderEntryConnectorUrl('CartPaymentCreate')
    AoeBraintree.apiKey = await GetApiKey()
    AoeBraintree.Initialize()

    // Add a listener to convert the display value to decimal format before Braintree reads it
    // This ensures Braintree always gets "106.90" instead of "106,90 €"
    await new Promise(resolve => setTimeout(resolve, 100))
    const submitButton = document.querySelector('#aoetbtSubmit')
    if (submitButton) {
      submitButton.addEventListener('click', () => {
        // Show loading state using global spinner
        SetIsLoading(true)
        // Convert formatted currency to plain decimal before Braintree reads the value
        const input = document.querySelector('#aoetbtPaymentAmount') as HTMLInputElement
        if (input) {
          input.value = charge.value.toFixed(2)
        }
      }, { capture: true }) // Use capture phase to run before Braintree's handler
    }

    // Listen for payment completion or error messages
    const messageHandler = (e: MessageEvent) => {
      if (
        e.data &&
        (e.data.messageType === 'aoepaymentcreated' ||
          e.data.messageType === 'aoepaymentcreationerror' ||
          e.data.messageType === 'aoepaymentcreation3dsusercanceled')
      ) {
        SetIsLoading(false)
      }
    }
    window.addEventListener('message', messageHandler)

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('message', messageHandler)
    }
  }
})


</script>
<style lang="scss">
#aoetbt {
  .form-control {
    height: calc(1.5em + 0.75rem + 2px);
  }
}
</style>
